// src/pages/admin/AdminProyecto/TabInversion.js
//
// Tab 3: Propuesta de inversión — conceptos clave, uso de la plata,
// estructura en 2 cuotas, tramos A/B/C, instrumento único
// (convertible participativo) con 4 escenarios de retorno.

import React from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, Alert, Chip, Divider,
} from '@mui/material';

const BRAND = { primary: '#2E7D32', dark: '#1B5E20', accent: '#F57C00', danger: '#C62828' };

const Metric = ({ label, value, detail, color = BRAND.primary }) => (
  <Paper elevation={0} sx={{
    p: 2, borderRadius: 2, borderLeft: 4, borderColor: color, bgcolor: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)', height: '100%',
  }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    {detail && (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>{detail}</Typography>
    )}
  </Paper>
);

const H2 = ({ children }) => (
  <Typography variant="h5" sx={{
    color: BRAND.dark, fontWeight: 700, mt: 4, mb: 2, pb: 1,
    borderBottom: 2, borderColor: BRAND.primary,
  }}>
    {children}
  </Typography>
);

const H3 = ({ children }) => (
  <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 600, mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

const TermBox = ({ children }) => (
  <Box sx={{
    bgcolor: '#E1F5FE', borderLeft: 3, borderColor: '#0277BD',
    p: 1.5, my: 1.5, borderRadius: 1, color: '#01579B', fontSize: 14,
  }}>
    {children}
  </Box>
);

const CalcBox = ({ children }) => (
  <Box sx={{
    bgcolor: '#FAFAFA', p: 1.5, my: 1, borderRadius: 1,
    fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-line',
  }}>
    {children}
  </Box>
);

const ScenarioBox = ({ title, description, calc }) => (
  <Paper elevation={0} sx={{
    p: 2, my: 1.5, border: 1, borderColor: '#E0E0E0', borderRadius: 2, bgcolor: '#fff',
  }}>
    <Typography variant="subtitle1" sx={{ color: BRAND.dark, fontWeight: 700 }}>{title}</Typography>
    {description && <Typography variant="body2" sx={{ mb: 1 }}>{description}</Typography>}
    {calc && <CalcBox>{calc}</CalcBox>}
  </Paper>
);

export default function TabInversion() {
  return (
    <Box>
      {/* Card destacada */}
      <Card sx={{
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
        borderLeft: 4, borderColor: BRAND.primary, mb: 3,
      }}>
        <CardContent>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 700, mb: 1.5 }}>
            Propuesta de inversión inicial
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            HolaVeci busca un <strong>aporte inicial de $10 millones COP</strong>, entregado en <strong>dos cuotas de $5 millones</strong>:
            la primera al firmar el acuerdo, la segunda al mes 2. Este aporte cubre todo el capital necesario hasta el punto
            de equilibrio operativo (mes 7).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><Metric color={BRAND.accent} label="Aporte solicitado"     value="$10M"    detail="COP (2 cuotas de $5M)" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric                       label="Rentabilidad esperada" value="3.7x"    detail="en 3 años (base)" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric                       label="Rentabilidad máxima"   value="Hasta 7x" detail="si HolaVeci se dispara" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric color="#388E3C"       label="Piso protegido"        value="~2x"      detail="Capital + rentabilidad mínima" /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Conceptos */}
      <H2>Antes de la propuesta: los conceptos que hay que entender</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Este documento usa varios términos técnicos del mundo de la inversión. Vale la pena explicarlos con calma antes
          de mostrar el instrumento propuesto.
        </Typography>

        <H3>¿Qué es "valoración pre-money"?</H3>
        <TermBox>
          Es <strong>cuánto vale HolaVeci HOY, antes de que entre plata nueva</strong>. Se estima en $150 millones COP
          considerando: código y aplicación ya desarrollada, arquitectura moderna, documentación estratégica completa,
          marca diseñada, y trabajo acumulado del founder durante más de un año.
        </TermBox>

        <H3>¿Qué es "valoración post-money"?</H3>
        <TermBox>
          Es <strong>cuánto vale HolaVeci DESPUÉS de que entre la plata nueva</strong>. Fórmula: pre-money + aporte.
          En este caso: $150M + $10M = <strong>$160M post-money</strong>.
        </TermBox>

        <H3>¿Qué es "convertible participativo con opción de salida"?</H3>
        <TermBox>
          Es <strong>un instrumento financiero híbrido</strong> que combina: préstamo con interés bajo, participación
          sobre las utilidades del negocio, y una opción de compra de acciones al final del periodo.
          <br /><br />
          El inversionista aporta plata hoy y durante 3 años recibe dos ingresos (intereses fijos + porcentaje de utilidades).
          Al final del año 3 decide si quiere ser socio comprando acciones a precio preferencial, o cobrar sus $10M
          de vuelta y cerrar la relación.
        </TermBox>

        <H3>¿Qué es "utilidad neta"?</H3>
        <TermBox>
          Es <strong>lo que le queda al negocio cada mes después de pagar TODOS los costos</strong>. Ejemplo: si HolaVeci factura
          $10M y gasta $8M, la utilidad neta es $2M. Con participación del 7%, el inversionista recibe $140.000 ese mes.
          Es distinto de "revenue" (ingresos brutos).
        </TermBox>

        <H3>¿Qué es la "opción de compra al año 3"?</H3>
        <TermBox>
          Es <strong>el derecho, no la obligación, de comprar acciones al final del año 3</strong>. Después de 3 años de
          reportes mensuales el inversionista decide:
          <br /><br />
          <strong>Camino A:</strong> "No quiero ser socio". HolaVeci le devuelve los $10M en efectivo.
          <br />
          <strong>Camino B:</strong> "Sí quiero ser socio". Convierte los $10M + aporta $5M adicionales = $15M totales,
          y recibe el 5% del equity a valoración pre-money de $200M (precio favorable).
        </TermBox>

        <H3>¿Qué es "periodo de gracia"?</H3>
        <TermBox>
          Plazo inicial donde HolaVeci no paga los intereses corrientes. En esta estructura son 2 meses de gracia.
          Los intereses acumulados de esos 2 meses ($300.000) se pagan a partir del mes 3.
        </TermBox>

        <H3>¿Qué es "costo de desarrollo"?</H3>
        <TermBox>
          Valor del trabajo técnico del founder-desarrollador. Un dev senior en Colombia cuesta $6M-$12M mensuales
          en el mercado formal. HolaVeci lo estima en $1.5M/mes los primeros 3 meses.
        </TermBox>
      </CardContent></Card>

      {/* Uso de la plata */}
      <H2>¿Para qué se va a usar la plata?</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>Distribución del aporte $10M:</Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: BRAND.primary }}>
              {['Rubro', 'Monto (COP)', 'Explicación'].map(h =>
                <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell colSpan={3}><strong>COSTOS TÉCNICOS Y DE ARRANQUE</strong></TableCell>
            </TableRow>
            {[
              ['Legalización (RUT, cámara, facturación DIAN)', '$800.000',   'Formalización tributaria de la SAS'],
              ['Play Store + reserva App Store',                '$500.000',   'Cuentas developer Google y Apple'],
              ['Dominio holaveci.co + hosting 6 meses',        '$700.000',   'Dominio anual + hosting web'],
              ['GCP + Supabase (8 meses plan mínimo)',         '$1.400.000', 'Cloud Run + base de datos'],
              ['Herramientas (Wompi, contador, DocuSign)',      '$600.000',   'Pasarela, facturación, firma digital'],
              ['Material físico primer barrio piloto',          '$400.000',   'Afiches, stickers, QR físicos'],
            ].map(([a, b, c]) => (
              <TableRow key={a}><TableCell>{a}</TableCell><TableCell>{b}</TableCell><TableCell>{c}</TableCell></TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#FFF3E0' }}>
              <TableCell><strong>Subtotal técnico</strong></TableCell><TableCell><strong>$4.400.000</strong></TableCell><TableCell><strong>44% del aporte</strong></TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell colSpan={3}><strong>COSTO DE DESARROLLO (3 MESES)</strong></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Costo del founder-desarrollador</TableCell><TableCell>$4.500.000</TableCell><TableCell>$1.5M × 3 meses, valor austero</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#FFF3E0' }}>
              <TableCell><strong>Subtotal desarrollo</strong></TableCell><TableCell><strong>$4.500.000</strong></TableCell><TableCell><strong>45% del aporte</strong></TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell colSpan={3}><strong>BUFFER Y CONTINGENCIAS</strong></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Reserva imprevistos + trámites no anticipados</TableCell><TableCell>$1.100.000</TableCell><TableCell>11% de buffer</TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#C8E6C9' }}>
              <TableCell><strong>TOTAL</strong></TableCell><TableCell><strong>$10.000.000</strong></TableCell><TableCell><strong>Cubre desarrollo + infra hasta break-even</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <TermBox>
          <strong>¿Qué pasa después del mes 3?</strong> HolaVeci ya genera ingresos propios que empiezan a cubrir el
          desarrollo. Los primeros meses el founder recibe menos de $1.5M; recibe lo que HolaVeci genere después de
          cubrir costos técnicos. Desde el mes 6-7 el revenue cubre completamente los costos incluyendo el desarrollo.
        </TermBox>
      </CardContent></Card>

      {/* 2 cuotas */}
      <H2>Estructura de aporte en 2 cuotas</H2>
      <Card sx={{ bgcolor: '#FFF3E0', borderLeft: 4, borderColor: BRAND.accent }}>
        <CardContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            El aporte se estructura en <strong>dos cuotas iguales de $5.000.000 cada una</strong>:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#FAFAFA' }}>
                <Typography variant="subtitle2" sx={{ color: BRAND.dark, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                  Cuota 1 — Al firmar el acuerdo
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: 14 }}>
                  <li><strong>Monto:</strong> $5.000.000 COP</li>
                  <li>Legalización y trámites tributarios</li>
                  <li>Publicación en Play Store</li>
                  <li>Dominio, hosting inicial y GCP</li>
                  <li>Material físico primer barrio piloto</li>
                  <li>Desarrollo del founder mes 1 ($1.5M)</li>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#FAFAFA' }}>
                <Typography variant="subtitle2" sx={{ color: BRAND.dark, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                  Cuota 2 — Al mes 2
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: 14 }}>
                  <li><strong>Monto:</strong> $5.000.000 COP</li>
                  <li>Herramientas adicionales (Wompi, contador, DocuSign)</li>
                  <li>Desarrollo del founder meses 2 y 3</li>
                  <li>Buffer de contingencias</li>
                  <li>Reserva para escalado si va según plan</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Ventaja inversionista:</strong> reduce exposición inicial. Al mes 2 ya puede ver avances tangibles
            antes de completar el desembolso.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Ventaja proyecto:</strong> los primeros $5M cubren arranque técnico y legal. La segunda cuota llega justo
            cuando se necesita.
          </Typography>
        </CardContent>
      </Card>

      {/* Tramos */}
      <H2>Estructura de tramos para múltiples inversionistas</H2>
      <Card sx={{ bgcolor: '#FFF3E0', borderLeft: 4, borderColor: BRAND.accent }}>
        <CardContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            El documento se puede presentar a varios inversionistas, pero <strong>no todos reciben el mismo instrumento</strong>.
            HolaVeci define cupos por tramos para preservar la sostenibilidad y proteger el equilibrio entre inversionistas.
          </Typography>

          <TermBox>
            <strong>¿Por qué existen los tramos?</strong> Si se ofreciera el mismo esquema estrella a todos, el % total
            sobre utilidades cedidas y el equity comprometido crecería hasta hacer inviable el modelo. Los tramos aseguran
            que el instrumento más generoso se reserva para el primer capital (mayor riesgo).
          </TermBox>

          <ScenarioBox
            title="Tramo A: primeros $10M COP totales"
            description="Instrumento: Convertible participativo con opción de compra de equity al año 3."
            calc={`• Interés fijo mensual: 1.5% (~18% anual)
• Participación sobre utilidades netas: 7% mensual
• Opción al año 3: comprar 5% del equity a valoración pre-money de $200M
• Rentabilidad esperada: 3.72x a 7x en 3 años

Reservado exclusivamente para el primer aporte hasta completar $10M.`}
          />

          <ScenarioBox
            title="Tramo B: aportes adicionales entre $10M y $25M"
            description="Instrumento: Préstamo mercantil simple con retorno fijo."
            calc={`• Retorno fijo: 20% anual (~1.67% mensual)
• Plazo: 24 a 30 meses
• Pagos: trimestrales o al vencimiento
• Sin participación sobre utilidades
• Sin opción de compra de equity
• Rentabilidad total: 1.5x a 1.67x

Retorno superior al CDT tradicional (8-10%) pero sin upside.`}
          />

          <ScenarioBox
            title="Tramo C: aportes por encima de $25M"
            description="Instrumento: Revenue share (participación en ingresos) o negociación específica."
            calc={`• Pago mensual: 3-4% del revenue bruto de HolaVeci
• Duración: hasta devolver 1.7x-2x el capital
• Sin participación sobre utilidades netas
• Sin opción de compra de equity
• Rentabilidad total: 1.7x-2x en 24-36 meses

Negociación caso a caso. No es tramo abierto por defecto.`}
          />

          <H3>Tabla comparativa entre tramos</H3>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: BRAND.primary }}>
                  {['Característica', 'Tramo A (primeros $10M)', 'Tramo B ($10M-$25M)', 'Tramo C (>$25M)'].map(h =>
                    <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ['Instrumento',                    'Convertible participativo', 'Préstamo simple',   'Revenue share'],
                  ['Interés fijo mensual',            '1.5%',                       '~1.67% (efectivo)', 'No aplica'],
                  ['Participación en utilidades',     '7% mensual',                 'No',                 'No'],
                  ['Participación en revenue',        'No',                         'No',                 '3-4% mensual'],
                  ['Opción de compra de equity',      'Sí (5% al año 3)',           'No',                 'No'],
                  ['Rentabilidad total esperada',     '3.72x a 7x',                 '1.5x a 1.67x',       '1.7x a 2x'],
                  ['Riesgo del inversionista',        'Medio-alto (piso protegido)','Bajo',               'Bajo-medio'],
                  ['Plazo típico',                    '36 meses',                   '24-30 meses',        '24-36 meses'],
                ].map(([a, b, c, d]) => (
                  <TableRow key={a}><TableCell><strong>{a}</strong></TableCell><TableCell>{b}</TableCell><TableCell>{c}</TableCell><TableCell>{d}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <H3>Justificación estructural</H3>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>El primer capital asume el mayor riesgo y recibe el mayor upside potencial.</strong> Los tramos posteriores
            tienen riesgo menor porque llegan cuando HolaVeci ya está operando, por lo cual reciben rentabilidad más
            limitada pero también más predecible.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Esta estructura protege tres cosas al mismo tiempo:</Typography>
          <Box component="ul" sx={{ pl: 3, fontSize: 14 }}>
            <li><strong>Al primer inversionista:</strong> su instrumento único no se diluye por invitaciones posteriores.</li>
            <li><strong>A HolaVeci:</strong> el % de utilidades cedidas se mantiene topado en 7% total.</li>
            <li><strong>Al fundador:</strong> el equity cedido al año 3 se mantiene topado en 5%.</li>
          </Box>
        </CardContent>
      </Card>

      {/* Instrumento único */}
      <H2>Estructura del aporte</H2>
      <Card sx={{
        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
        borderLeft: 4, borderColor: BRAND.accent,
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 700, mb: 1.5 }}>
            Convertible participativo con opción de salida al año 3
          </Typography>
          <Chip label="Instrumento propuesto" sx={{ bgcolor: '#FFF3E0', color: BRAND.accent, fontWeight: 600, mb: 2 }} />

          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>En palabras simples:</strong> el inversionista aporta $10M ahora y durante 3 años recibe dos ingresos:
            un interés fijo mensual pequeño y una participación del 7% sobre las utilidades netas mensuales. Al final del
            año 3 escoge: recibir $10M de vuelta y cerrar, o comprar el 5% de HolaVeci aportando $5M adicionales.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#FAFAFA' }}>
                <Typography variant="subtitle2" sx={{ color: BRAND.dark, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                  Fase 1 (años 1 a 3)
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: 14 }}>
                  <li><strong>Aporte inicial:</strong> $10M COP (2 cuotas de $5M)</li>
                  <li><strong>Interés fijo mensual:</strong> 1.5% sobre $10M = $150.000/mes</li>
                  <li><strong>Participación mensual:</strong> 7% de las utilidades netas</li>
                  <li><strong>Gracia inicial:</strong> 2 meses sin pago de intereses</li>
                  <li><strong>Duración:</strong> 36 meses</li>
                  <li><strong>Reporte contable mensual</strong> con desglose</li>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#FAFAFA' }}>
                <Typography variant="subtitle2" sx={{ color: BRAND.dark, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                  Al final del año 3
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: 14 }}>
                  <li><strong>Camino A:</strong> devolución de $10M cash. Fin.</li>
                  <li><strong>Camino B:</strong> $10M + $5M adicionales = $15M totales</li>
                  <li><strong>A cambio recibe:</strong> 5% del equity</li>
                  <li><strong>Valoración pre-money aplicable:</strong> $200M (preferencial)</li>
                  <li><strong>Decisión unilateral</strong> del inversionista</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <TermBox>
            <strong>¿Utilidad neta?</strong> Lo que le queda a HolaVeci después de pagar TODOS los costos: hosting,
            concesionarios, desarrollo, impuestos, servicios. Si factura $10M y gasta $8M, la utilidad neta es $2M,
            y el inversionista recibe el 7% = $140.000 ese mes.
          </TermBox>

          <TermBox>
            <strong>Periodo de gracia:</strong> los primeros 2 meses HolaVeci NO paga intereses fijos (solo se acumulan).
            Tiempo para publicar la app y arrancar sin presión inmediata. Los $300.000 acumulados se pagan a partir del mes 3.
          </TermBox>

          <H3>Escenarios de retorno del inversionista en 3 años</H3>

          <ScenarioBox
            title="Escenario A: HolaVeci va según lo proyectado (valor año 3: ~$200M)"
            description="El inversionista NO ejerce la opción de compra. Recibe:"
            calc={`Intereses fijos en 3 años:                       $5.400.000
Participación 7% sobre utilidades acumuladas:     $21.840.000
Devolución de capital:                            $10.000.000
─────────────────────────────────────────────────────────────
Total recibido: $37.240.000 sobre $10M invertidos
Rentabilidad: 3.72x en 3 años (~55% anual efectivo)`}
          />

          <ScenarioBox
            title="Escenario B: HolaVeci crece más (valor año 3: ~$500M)"
            description="El inversionista SÍ ejerce la opción de compra."
            calc={`Intereses fijos:                    $5.400.000
Participación 7%:                   ~$28.000.000
En vez de $10M cash, convierte + aporta $5M
Recibe 5% de HolaVeci = $25M en equity
─────────────────────────────────────────────
Total: $33.4M + $25M equity − $5M = $53.4M
Sobre inversión total $15M: 3.56x en 3 años`}
          />

          <ScenarioBox
            title="Escenario C: HolaVeci se dispara (valor año 3: ~$1.000M)"
            description="El inversionista SÍ ejerce la opción de compra."
            calc={`Intereses fijos:                    $5.400.000
Participación 7%:                   ~$56.000.000
Convierte $10M + aporta $5M
Recibe 5% de HolaVeci = $50M en equity
─────────────────────────────────────────────
Total: $61.4M + $50M equity − $5M = $106.4M
Sobre inversión total $15M: 7x en 3 años`}
          />

          <ScenarioBox
            title="Escenario D: HolaVeci crece muy lento (valor año 3: ~$100M)"
            description="El inversionista NO ejerce la opción de compra."
            calc={`Intereses fijos:                    $5.400.000
Participación 7%:                   $4.200.000
Devolución de capital:              $10.000.000
─────────────────────────────────────────────
Total recibido: $19.6M sobre $10M invertidos
Rentabilidad: 1.96x en 3 años (piso protegido)`}
          />

          <H3>Ventajas clave de esta estructura</H3>
          <Box component="ul" sx={{ pl: 3, fontSize: 14 }}>
            <li><strong>Rentabilidad escalable:</strong> más crece HolaVeci, más recibe el inversionista sin renegociar.</li>
            <li><strong>Piso protegido:</strong> peor escenario recupera capital + rentabilidad razonable.</li>
            <li><strong>Techo potencial muy alto:</strong> si HolaVeci se dispara, hasta 7x.</li>
            <li><strong>Decisión informada al año 3:</strong> tras 3 años de reportes decide con datos reales.</li>
            <li><strong>Descuento preferencial:</strong> $200M pre-money es favorable respecto al valor real.</li>
            <li><strong>Sin obligación de convertirse en socio:</strong> puede cobrar y retirarse.</li>
          </Box>

          <H3>Comparación con préstamo tradicional al 7% mensual</H3>
          <CalcBox>{`Modelo tradicional (7% mensual × 36 meses sobre $10M):
Intereses: $25.200.000  +  Capital: $10.000.000
Total: $35.200.000 sobre $10M = 3.52x

Este modelo (base HolaVeci):
Total: $37.240.000 sobre $10M = 3.72x
+ Opción de comprar equity si el negocio crece
+ Protección del capital si no crece`}</CalcBox>

          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', mt: 2 }}>
            Estructura ideal para inversionistas acostumbrados a rentabilidades altas de préstamo informal, que además
            quieren participar del upside con opción de convertirse en socios tras 3 años de operación verificable.
          </Typography>
        </CardContent>
      </Card>

      {/* Formalización */}
      <H2>Formalización del aporte</H2>
      <Card><CardContent>
        <H3>Instrumento aplicable</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          El convertible participativo con opción de salida al año 3 se formaliza mediante un <strong>contrato mercantil
          atípico</strong> firmado como documento privado entre las partes, que combina en un solo instrumento préstamo
          con participación en utilidades y opción de compra de equity.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          No requiere trámite societario ni reforma estatutaria al momento del aporte. La única formalización societaria
          se activa si al año 3 el inversionista ejerce la opción de compra de equity.
        </Typography>

        <H3>Compromiso permanente de HolaVeci con el inversionista</H3>
        <Box component="ul" sx={{ pl: 3, fontSize: 14 }}>
          <li><strong>Reporte mensual escrito</strong> con métricas clave: tenderos activos, pedidos completados, revenue, gastos, uso de fondos.</li>
          <li><strong>Sesión mensual de preguntas</strong> por videollamada de 30 minutos para revisar el reporte.</li>
          <li><strong>Acceso al dashboard operativo</strong> con datos en tiempo real (cuando esté disponible).</li>
          <li><strong>Aviso inmediato</strong> ante cualquier cambio material del negocio.</li>
          <li><strong>Confidencialidad recíproca</strong>: toda información compartida está cubierta por acuerdo de confidencialidad.</li>
        </Box>
      </CardContent></Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Este documento</strong> presenta las estructuras disponibles para el aporte inicial. Cada estructura se
        formaliza mediante documento escrito firmado por las partes. Los retornos son estimaciones basadas en escenarios
        modelados; los resultados reales dependen del desempeño del negocio. La valoración pre-money de $150M refleja
        el trabajo, código y arquitectura acumulados a agosto de 2026.
      </Alert>
    </Box>
  );
}
